import { useEffect, useState, useCallback } from "react";

export function useAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get token from localStorage
  const getToken = useCallback(() => localStorage.getItem("token"), []);

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [getToken]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => !!getToken(), [getToken]);

  // Fetch all agents
  useEffect(() => {
    const controller = new AbortController();
    const fetchAgents = async () => {
      if (!isAuthenticated()) {
        setError("Please sign in to view agents");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${process.env.REACT_APP_SERVER_PATH}/delivery-agents`,
          {
            method: "GET",
            headers: getAuthHeaders(),
            signal: controller.signal,
          }
        );

        console.log("Agents API response status:", response.status);

        if (!response.ok) {
          throw new Error(`Failed to fetch agents (${response.status})`);
        }

        const data = await response.json();
        console.log("Fetched agents:", data);

        // Return raw agents data
        const rawAgents = Array.isArray(data) ? data : [];
        setAgents(rawAgents);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Fetch agents error:", err);
          setError(err.message || "Failed to fetch agents");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
    return () => controller.abort();
  }, [getAuthHeaders, isAuthenticated]);

  // Create new agent
  const createAgent = async (agentData) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/delivery-agents`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(agentData),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create agent (${response.status})`);
      }

      const newAgent = await response.json();
      setAgents((prevAgents) => [...prevAgents, newAgent]);
      return newAgent;
    } catch (error) {
      console.error("Create agent error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update agent
  const updateAgent = async (agentId, updateData) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/delivery-agents/${agentId}`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update agent (${response.status})`);
      }

      const updatedAgent = await response.json();
      setAgents((prevAgents) =>
        prevAgents.map((agent) =>
          agent._id === agentId ? updatedAgent : agent
        )
      );
      return updatedAgent;
    } catch (error) {
      console.error("Update agent error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete agent
  const deleteAgent = async (agentId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/delivery-agents/${agentId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete agent (${response.status})`);
      }

      setAgents((prevAgents) =>
        prevAgents.filter((agent) => agent._id !== agentId)
      );
      return true;
    } catch (error) {
      console.error("Delete agent error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get agent statistics
  const getAgentStats = () => {
    return {
      total: agents.length,
      active: agents.filter(
        (agent) => agent.status === "active" && agent.isActive
      ).length,
      inactive: agents.filter(
        (agent) => agent.status === "inactive" || !agent.isActive
      ).length,
      busy: agents.filter((agent) => agent.status === "busy").length,
    };
  };

  // Refresh agents
  const refreshAgents = () => {
    window.location.reload();
  };

  // Clear error
  const clearError = () => setError(null);

  return {
    agents,
    loading,
    error,
    createAgent,
    updateAgent,
    deleteAgent,
    getAgentStats,
    refreshAgents,
    clearError,
    isAuthenticated: isAuthenticated(),
  };
}
